import React from 'react';
import { Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

// Client-side CSV export for console list pages (docs §UX: export across list
// pages). Generates the file in-browser from data already on screen — no server
// round-trip. RFC-4180 quoting (wrap + double any field containing "," '"' or newline).

export interface CsvColumn<T> {
  /** Property key on the row (used when `value` is omitted). */
  key?: keyof T | string;
  header: string;
  /** Custom cell extractor — use for computed/nested values. */
  value?: (row: T) => unknown;
}

const esc = (v: unknown): string => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function exportToCsv<T>(filename: string, rows: T[], columns: CsvColumn<T>[]): void {
  const header = columns.map((c) => esc(c.header)).join(',');
  const body = rows.map((r) =>
    columns.map((c) => esc(c.value ? c.value(r) : (r as Record<string, unknown>)[c.key as string])).join(','),
  );
  // Prepend a BOM so Excel opens UTF-8 correctly.
  const csv = '﻿' + [header, ...body].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function ExportButton<T>({
  filename,
  rows,
  columns,
  disabled,
}: {
  filename: string;
  rows: T[];
  columns: CsvColumn<T>[];
  disabled?: boolean;
}) {
  return (
    <Button
      icon={<DownloadOutlined />}
      disabled={disabled || rows.length === 0}
      onClick={() => exportToCsv(filename, rows, columns)}
    >
      Export CSV
    </Button>
  );
}
